import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  BookOpen,
  Star,
  ExternalLink,
  ArrowUpRight,
  Calendar,
  ChevronRight,
  Sparkles,
  BarChart3,
  Receipt,
  CreditCard,
  Filter,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  paymentsApi,
  DetailedEarningsData,
  CourseEarningBreakdown,
  EarningsTransaction,
  usersApi,
} from '../../lib/api';
import { formatPrice } from '../../lib/utils';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

type PeriodFilter = 'all' | '12months' | '6months' | '3months' | 'thisMonth';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'thisMonth', label: 'This Month' },
  { value: '3months', label: 'Last 3 Months' },
  { value: '6months', label: 'Last 6 Months' },
  { value: '12months', label: 'Last 12 Months' },
  { value: 'all', label: 'All Time' },
];

export default function EarningsPage() {
  const [data, setData] = useState<DetailedEarningsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState<{
    hasAccount: boolean;
    isOnboarded: boolean;
    payoutsEnabled?: boolean;
  } | null>(null);
  const [isOpeningHeaderDashboard, setIsOpeningHeaderDashboard] = useState(false);
  const [isOpeningBannerDashboard, setIsOpeningBannerDashboard] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'courses' | 'transactions'>('overview');
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('thisMonth');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [joinDate, setJoinDate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [earningsRes, connectRes, profileRes] = await Promise.all([
          paymentsApi.getDetailedEarnings(),
          paymentsApi.getConnectStatus().catch(() => null),
          usersApi.getProfile().catch(() => null),
        ]);

        if (earningsRes.data.success && earningsRes.data.data) {
          setData(earningsRes.data.data);
        }
        if (connectRes?.data.success && connectRes.data.data) {
          setConnectStatus(connectRes.data.data);
        }
        if (profileRes?.data.success && profileRes.data.data) {
          const profile = profileRes.data.data as { createdAt?: string };
          if (profile.createdAt) {
            setJoinDate(new Date(profile.createdAt));
          }
        }
      } catch (error) {
        console.error('Failed to fetch earnings:', error);
        toast.error('Failed to load earnings data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenDashboard = async (source: 'header' | 'banner') => {
    const setLoading = source === 'header' ? setIsOpeningHeaderDashboard : setIsOpeningBannerDashboard;
    
    if (!connectStatus?.hasAccount || !connectStatus?.isOnboarded) {
      try {
        setLoading(true);
        const response = await paymentsApi.connectOnboard({
          returnUrl: `${window.location.origin}/professor/earnings`,
          refreshUrl: `${window.location.origin}/professor/earnings?refresh=true`,
        });

        if (response.data.success && response.data.data?.onboardingUrl) {
          window.location.href = response.data.data.onboardingUrl;
        } else {
          toast.error('Failed to start payment setup');
        }
      } catch (error) {
        console.error('Failed to start onboarding:', error);
        toast.error('Failed to start payment setup');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      const response = await paymentsApi.getDashboardLink();

      if (response.data.success && response.data.data?.url) {
        window.open(response.data.data.url, '_blank');
      } else {
        toast.error('Failed to open payout dashboard');
      }
    } catch (error) {
      console.error('Failed to get dashboard link:', error);
      toast.error('Failed to open payout dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatMonth = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  const formatShortMonth = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
    });
  };

  // Filter monthly trend data based on selected period and join date
  const getFilteredMonthlyTrend = () => {
    if (!data?.monthlyTrend) return [];

    let filtered = [...data.monthlyTrend];
    const now = new Date();

    // Apply period filter
    if (periodFilter !== 'all') {
      let cutoffDate: Date;
      
      if (periodFilter === 'thisMonth') {
        // Show only current month
        cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
      } else {
        const monthsBack = {
          '12months': 12,
          '6months': 6,
          '3months': 3,
        }[periodFilter] || 12;

        cutoffDate = new Date(now.getFullYear(), now.getMonth() - monthsBack + 1, 1);
      }

      filtered = filtered.filter((m) => new Date(m.month) >= cutoffDate);
    }

    // Filter out data before join date
    if (joinDate) {
      const joinMonth = new Date(joinDate.getFullYear(), joinDate.getMonth(), 1);
      filtered = filtered.filter((m) => new Date(m.month) >= joinMonth);
    }

    return filtered;
  };

  // Generate Y-axis labels
  const getYAxisLabels = (maxValue: number) => {
    if (maxValue === 0) return ['$0'];

    const steps = 4;
    const stepValue = Math.ceil(maxValue / steps);
    const labels = [];

    for (let i = steps; i >= 0; i--) {
      const value = stepValue * i;
      if (value >= 1000) {
        labels.push(`$${(value / 1000).toFixed(1)}k`);
      } else {
        labels.push(`$${value}`);
      }
    }

    return labels;
  };

  if (isLoading) {
    return (
      <div className="space-y-8 max-w-7xl">
        <div className="skeleton h-24 rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-32 rounded-2xl" />
          ))}
        </div>
        <div className="skeleton h-80 rounded-2xl" />
      </div>
    );
  }

  const overview = data?.overview;
  const isPositiveGrowth = (overview?.monthlyGrowth || 0) >= 0;
  const filteredTrend = getFilteredMonthlyTrend();
  const maxEarnings = filteredTrend.length > 0 ? Math.max(...filteredTrend.map((m) => m.earnings)) : 0;
  const yAxisLabels = getYAxisLabels(maxEarnings);

  return (
    <motion.div
      initial="initial"
      animate="animate"
      className="space-y-8 max-w-7xl"
    >
      {/* Header */}
      <motion.div
        variants={fadeUp}
        transition={{ duration: 0.4 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-medium text-amber-400 uppercase tracking-wider">
              Revenue
            </span>
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Earnings</h1>
          <p className="mt-1 text-white/50 text-sm">
            Track your revenue, monitor course performance, and manage payouts
          </p>
        </div>

        {/* Premium Payout Button */}
        <button
          onClick={() => handleOpenDashboard('header')}
          disabled={isOpeningHeaderDashboard}
          className="group relative flex items-center gap-3 px-6 py-3.5 rounded-2xl font-semibold text-sm
                   overflow-hidden transition-all duration-500 disabled:opacity-70"
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            boxShadow: '0 0 30px rgba(251, 191, 36, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          }}
        >
          {/* Animated gradient overlay */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
            }}
          />

          {/* Shine effect */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
            <div
              className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
              }}
            />
          </div>

          <div className="relative z-10 flex items-center gap-3">
            {isOpeningHeaderDashboard ? (
              <>
                <div className="w-5 h-5 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                <span className="text-white">Opening...</span>
              </>
            ) : (
              <>
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                    boxShadow: '0 4px 15px rgba(251, 191, 36, 0.4)',
                  }}
                >
                  <CreditCard className="w-4.5 h-4.5 text-black" />
                </div>
                <div className="text-left">
                  <div className="text-white font-semibold">
                    {!connectStatus?.hasAccount
                      ? 'Set Up Payouts'
                      : !connectStatus?.isOnboarded
                      ? 'Complete Setup'
                      : 'Manage Payouts'}
                  </div>
                  <div className="text-amber-400/70 text-xs font-normal">
                    {!connectStatus?.isOnboarded ? 'Connect your bank account' : 'View balance & withdraw'}
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-amber-400/50 group-hover:text-amber-400 transition-colors ml-2" />
              </>
            )}
          </div>
        </button>
      </motion.div>

      {/* Stats Overview Cards */}
      <motion.div
        variants={fadeUp}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {/* Lifetime Earnings */}
        <div
          className="p-6 rounded-2xl"
          style={{
            background:
              'linear-gradient(145deg, rgba(251, 191, 36, 0.1) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
            <DollarSign className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-white">
            {formatPrice(overview?.lifetimeEarnings || 0)}
          </div>
          <div className="text-sm text-white/50 mt-1">Lifetime Earnings</div>
        </div>

        {/* This Month */}
        <div
          className="p-6 rounded-2xl"
          style={{
            background:
              'linear-gradient(145deg, rgba(34, 197, 94, 0.1) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
            <Calendar className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-white">
            {formatPrice(overview?.currentMonthEarnings || 0)}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-white/50">This Month</span>
            {overview?.monthlyGrowth !== undefined && overview.monthlyGrowth !== 0 && (
              <span
                className={`flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                  isPositiveGrowth
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-red-500/20 text-red-400'
                }`}
              >
                {isPositiveGrowth ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {Math.abs(overview.monthlyGrowth)}%
              </span>
            )}
          </div>
        </div>

        {/* Total Sales */}
        <div
          className="p-6 rounded-2xl"
          style={{
            background:
              'linear-gradient(145deg, rgba(59, 130, 246, 0.1) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
            <Receipt className="w-5 h-5 text-blue-400" />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-white">
            {overview?.lifetimeSales || 0}
          </div>
          <div className="text-sm text-white/50 mt-1">Total Sales</div>
        </div>

        {/* This Month Sales */}
        <div
          className="p-6 rounded-2xl"
          style={{
            background:
              'linear-gradient(145deg, rgba(168, 85, 247, 0.1) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
            <Users className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-2xl lg:text-3xl font-bold text-white">
            {overview?.currentMonthSales || 0}
          </div>
          <div className="text-sm text-white/50 mt-1">Sales This Month</div>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div
        variants={fadeUp}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="flex gap-2 border-b border-white/10 pb-0"
      >
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'courses', label: 'By Course', icon: BookOpen },
          { id: 'transactions', label: 'Transactions', icon: CreditCard },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-[1px] ${
              activeTab === tab.id
                ? 'text-amber-400 border-amber-400'
                : 'text-white/50 border-transparent hover:text-white/70'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </motion.div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Monthly Trend Chart */}
          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="lg:col-span-2 p-6 rounded-2xl"
            style={{
              background:
                'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            {/* Chart Header with Filter */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Monthly Earnings</h3>

              {/* Period Filter Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                           bg-white/5 border border-white/10 text-white/70 hover:text-white 
                           hover:bg-white/10 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  {PERIOD_OPTIONS.find((p) => p.value === periodFilter)?.label}
                  <ChevronDown className="w-4 h-4" />
                </button>

                {showPeriodDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowPeriodDropdown(false)}
                    />
                    <div
                      className="absolute right-0 top-full mt-2 w-48 rounded-xl overflow-hidden z-20"
                      style={{
                        background: 'linear-gradient(145deg, #1a1a2e 0%, #0f0f14 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      {PERIOD_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setPeriodFilter(option.value);
                            setShowPeriodDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                            periodFilter === option.value
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'text-white/70 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {filteredTrend.length > 0 ? (
              <div className="space-y-4">
                {/* Chart with Y-axis */}
                <div className="flex gap-4">
                  {/* Y-axis labels */}
                  <div className="flex flex-col justify-between h-48 text-right pr-2">
                    {yAxisLabels.map((label, index) => (
                      <span key={index} className="text-xs text-white/40 leading-none">
                        {label}
                      </span>
                    ))}
                  </div>

                  {/* Bar chart */}
                  <div className="flex-1 relative">
                    {/* Horizontal grid lines */}
                    <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                      {yAxisLabels.map((_, index) => (
                        <div key={index} className="border-t border-white/5 w-full" />
                      ))}
                    </div>

                    {/* Bars */}
                    <div className="flex items-end gap-2 h-48 relative z-10">
                      {filteredTrend.map((month, index) => {
                        const heightPercent = maxEarnings > 0 ? (month.earnings / maxEarnings) * 100 : 0;
                        return (
                          <div
                            key={index}
                            className="flex-1 flex flex-col items-center justify-end h-full group"
                          >
                            {/* Tooltip */}
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-8 
                                          bg-black/90 text-white text-xs px-2 py-1 rounded pointer-events-none whitespace-nowrap z-20">
                              {formatPrice(month.earnings)} - {month.sales} sale{month.sales !== 1 ? 's' : ''}
                            </div>

                            {/* Bar */}
                            <div
                              className="w-full bg-gradient-to-t from-amber-600 to-amber-400 rounded-t-md 
                                       transition-all duration-500 hover:from-amber-500 hover:to-amber-300 cursor-pointer"
                              style={{
                                height: `${Math.max(heightPercent, 3)}%`,
                                minHeight: month.earnings > 0 ? '12px' : '4px',
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* X-axis labels */}
                <div className="flex gap-4">
                  <div className="w-12" /> {/* Spacer for Y-axis */}
                  <div className="flex-1 flex gap-2">
                    {filteredTrend.map((month, index) => (
                      <div key={index} className="flex-1 text-center">
                        <span className="text-xs text-white/40">
                          {filteredTrend.length <= 6 ? formatMonth(month.month) : formatShortMonth(month.month)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm text-white/50">
                    {joinDate && (
                      <span>Member since {formatDate(joinDate.toISOString())}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded bg-gradient-to-t from-amber-600 to-amber-400" />
                      <span className="text-white/70">Earnings</span>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center text-white/40">
                <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                <p>No earnings data for this period</p>
                {joinDate && (
                  <p className="text-xs mt-1">Member since {formatDate(joinDate.toISOString())}</p>
                )}
              </div>
            )}
          </motion.div>

          {/* Top Performers */}
          <motion.div
            variants={fadeUp}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="p-6 rounded-2xl"
            style={{
              background:
                'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <h3 className="text-lg font-semibold text-white mb-4">Top Performers</h3>

            {data?.topPerformers && data.topPerformers.length > 0 ? (
              <div className="space-y-3">
                {data.topPerformers.slice(0, 5).map((course, index) => (
                  <Link
                    key={course.id}
                    to={`/professor/courses/${course.id}/edit`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                  >
                    <span className="w-6 h-6 rounded-lg bg-amber-500/20 text-amber-400 text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate group-hover:text-amber-400 transition-colors">
                        {course.title}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/40">
                        <span className="flex items-center gap-0.5">
                          <Users className="w-3 h-3" />
                          {course.enrollments}
                        </span>
                        {course.rating && (
                          <span className="flex items-center gap-0.5">
                            <Star className="w-3 h-3 text-amber-400" />
                            {course.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-emerald-400">
                      {formatPrice(course.totalEarnings)}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-white/40 text-sm">
                No courses with sales yet
              </div>
            )}
          </motion.div>
        </div>
      )}

      {activeTab === 'courses' && (
        <motion.div
          variants={fadeUp}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="rounded-2xl overflow-hidden"
          style={{
            background:
              'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white">Earnings by Course</h3>
            <p className="text-sm text-white/50 mt-1">
              Detailed breakdown of revenue from each course
            </p>
          </div>

          {data?.courseBreakdown && data.courseBreakdown.length > 0 ? (
            <div className="divide-y divide-white/5">
              {data.courseBreakdown.map((course: CourseEarningBreakdown) => (
                <Link
                  key={course.id}
                  to={`/professor/courses/${course.id}/edit`}
                  className="flex items-center gap-4 p-4 hover:bg-white/[0.03] transition-colors group"
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                    {course.thumbnailUrl ? (
                      <img
                        src={course.thumbnailUrl}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-white/20" />
                      </div>
                    )}
                  </div>

                  {/* Course Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-white truncate group-hover:text-amber-400 transition-colors">
                        {course.title}
                      </h4>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          course.status === 'PUBLISHED'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}
                      >
                        {course.status.toLowerCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {course.totalEnrollments} enrolled
                      </span>
                      <span>{course.totalSales} sales</span>
                      {course.rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-amber-400" />
                          {course.rating.toFixed(1)} ({course.totalReviews})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Earnings */}
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-white">
                      {formatPrice(course.totalEarnings)}
                    </div>
                    {course.currentMonthEarnings > 0 && (
                      <div className="text-xs text-emerald-400">
                        +{formatPrice(course.currentMonthEarnings)} this month
                      </div>
                    )}
                  </div>

                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/40 flex-shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No courses yet</p>
              <Link
                to="/professor/courses/new"
                className="inline-flex items-center gap-2 mt-4 text-amber-400 hover:text-amber-300 text-sm font-medium"
              >
                Create your first course
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'transactions' && (
        <motion.div
          variants={fadeUp}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="rounded-2xl overflow-hidden"
          style={{
            background:
              'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white">Recent Transactions</h3>
            <p className="text-sm text-white/50 mt-1">Your latest course sales</p>
          </div>

          {data?.recentTransactions && data.recentTransactions.length > 0 ? (
            <div className="divide-y divide-white/5">
              {data.recentTransactions.map((tx: EarningsTransaction) => (
                <div key={tx.id} className="flex items-center gap-4 p-4">
                  {/* Student Avatar */}
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                    {tx.student.avatarUrl ? (
                      <img
                        src={tx.student.avatarUrl}
                        alt={tx.student.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-semibold text-sm">
                        {tx.student.name.charAt(0)}
                      </span>
                    )}
                  </div>

                  {/* Transaction Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white">
                      <span className="font-medium">{tx.student.name}</span>
                      <span className="text-white/50"> purchased </span>
                      <Link
                        to={`/professor/courses/${tx.course.id}/edit`}
                        className="font-medium text-amber-400 hover:text-amber-300"
                      >
                        {tx.course.title}
                      </Link>
                    </div>
                    <div className="text-xs text-white/40 mt-0.5">{formatDate(tx.createdAt)}</div>
                  </div>

                  {/* Amount */}
                  <div className="text-lg font-bold text-emerald-400 flex-shrink-0">
                    +{formatPrice(tx.amount)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Receipt className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">No transactions yet</p>
              <p className="text-white/30 text-sm mt-1">
                Sales will appear here when students purchase your courses
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* Payout Info Banner */}
      {!connectStatus?.isOnboarded && (
        <motion.div
          variants={fadeUp}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="p-6 rounded-2xl"
          style={{
            background:
              'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(15, 16, 20, 0.95) 50%, rgba(249, 115, 22, 0.1) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
          }}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-1">Set Up Your Payouts</h3>
              <p className="text-white/50 text-sm mb-4">
                Connect your bank account to receive payouts. You keep 80% of each sale - we handle
                payment processing, taxes, and more.
              </p>
              <button
                onClick={() => handleOpenDashboard('banner')}
                disabled={isOpeningBannerDashboard}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold
                         transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                  boxShadow: '0 4px 15px rgba(251, 191, 36, 0.3)',
                }}
              >
                {isOpeningBannerDashboard ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    <span className="text-black">Setting up...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 text-black" />
                    <span className="text-black">Complete Setup</span>
                    <ArrowUpRight className="w-4 h-4 text-black" />
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
